import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { Document, Font, Image, PDFViewer, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { customColors, theme } from 'theme';
import { currencyOf, resolve } from 'utils/util';
import workSansBold from './../../../assets/fonts/WorkSans-Bold.ttf';
import { FlowConsignadoImpressaoDTO } from './consignado-impressao.model';


const ConsignadoPDF = ({ dados, onClose }: { dados: FlowConsignadoImpressaoDTO; onClose: () => void; }) => {

    const header = () => {
        return (
            <View style={{
                flexDirection: 'row',
                justifyContent: "space-between",
            }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={[styles.minText, styles.bold]}>Estabelecimento: </Text>
                    <Text style={[styles.minText]}>{dados.estabelecimento}</Text>
                </View>

                <Text style={[styles.minText]}>
                    Impresso por {dados.usuario} em {dados.dataHora}
                </Text>
            </View>
        );
    };

    const getTitulo = (data: { title: string, bigFontSize?: boolean, visible?: boolean, centralizado?: boolean; }) => {
        data.visible = data.visible === undefined ? true : data.visible;
        return data.visible ? (
            <View style={{
                borderTop: "1px solid black",
                borderBottom: '1px solid black',
                backgroundColor: customColors.cinzaClaro,
                alignItems: data.centralizado ? 'center' : 'flex-start',
                paddingVertical: '2px',
            }}>
                <View>
                    <Text style={[styles.bold, { fontSize: data.bigFontSize ? 14 : 10 }]}>
                        {data.title}
                    </Text>
                </View>
            </View>
        ) : undefined;
    };

    const cabecalhoLeftSide = () => {
        return (
            <View style={{ width: "50%", gap: '1px' }}>
                <View style={[{ height: "auto", flexDirection: 'row' }]}>
                    <Text style={[styles.minText, { width: "50%", }]}>
                        Ordem: {dados.numero}
                    </Text>
                    <Text style={[styles.minText]}>
                        Situação: {dados.situacao}
                    </Text>
                </View>
                <Text style={[styles.minText]}>
                    Cliente: {dados.cliente}
                </Text>
                <Text style={[styles.minText]}>
                    Nome fantasia: {dados.clienteNomeFantasia}
                </Text>
                <Text style={[styles.minText]}>
                    Endereço: {dados.logradouro}
                </Text>
                <Text style={[styles.minText]}>
                    Complemento: {dados.complemento}
                </Text>
                <Text style={[styles.minText]}>
                    E-mail: {dados.email}
                </Text>
                <Text style={[styles.minText]}>
                    Vendedor: {dados.representante}
                </Text>
            </View>
        );
    };

    const cabecalhoRightSide = () => {
        return (
            <View style={{ width: "50%", gap: '1px' }}>
                <Text style={[styles.minText]}>
                    Emissão: {dados.emissao}
                </Text>
                <Text style={[styles.minText]}>
                    Tabela de preço: {dados.tabelaPreco}
                </Text>
                <Text style={[styles.minText]}>
                    Telefone: {dados.telefone}
                </Text>
                <Text style={[styles.minText]}>
                    Inscrição estadual: {dados.inscricaoEstadual}
                </Text>
                <Text style={[styles.minText]}>
                    Município: {dados.municipio}
                </Text>
                <Text style={[styles.minText]}>
                    CEP: {dados.cep}
                </Text>
                <Text style={[styles.minText]}>
                    Website: {dados.website}
                </Text>
                <Text style={[styles.minText]}>
                    Rede social: {dados.redeSocial}
                </Text>
            </View >
        );
    };

    const renderTotalizador = () => {
        const positivoOuZero = (value: number) => {
            return value < 0 ? 0 : value;
        };

        const abertos = (dados.abertos ?? [])
            .reduce((acc, curr) => {
                return {
                    quantidadeAberta: acc.quantidadeAberta + curr.quantidade,
                    valorAberto: acc.valorAberto + curr.valorTotalLiquido,
                };
            }, {
                quantidadeAberta: 0,
                valorAberto: 0,
            });

        const saida = (dados.notas?.notasSaida ?? [])
            .reduce((acc, curr) => {
                return {
                    quantidadeConsignada: acc.quantidadeConsignada + curr.quantidadeTotal,
                    valorConsignado: acc.valorConsignado + curr.valorTotal,
                };
            }, {
                quantidadeConsignada: 0,
                valorConsignado: 0,
            });

        const entrada = (dados.notas?.notasDevolucao ?? [])
            .reduce((acc, curr) => {
                return {
                    quantidadeDevolvida: acc.quantidadeDevolvida + curr.quantidadeTotal,
                    valorDevolvido: acc.valorDevolvido + curr.valorTotal,
                };
            }, {
                quantidadeDevolvida: 0,
                valorDevolvido: 0,
            });

        const vendidos = (dados.vendidos ?? [])
            .reduce((acc, curr) => {
                return {
                    quantidadeVendida: acc.quantidadeVendida + curr.quantidadeVendida,
                    valorVendido: acc.valorVendido + curr.valorLiquido,
                };
            }, {
                quantidadeVendida: 0,
                valorVendido: 0,
            });

        const cancelados = (dados.cancelados ?? [])
            .reduce((acc, curr) => {
                return {
                    quantidadeCancelada: acc.quantidadeCancelada + curr.quantidadeCancelada,
                    valorCancelado: acc.valorCancelado + curr.valorLiquido,
                };
            }, {
                quantidadeCancelada: 0,
                valorCancelado: 0,
            });

        const quantidadeDevolvida = positivoOuZero(saida.quantidadeConsignada - vendidos.quantidadeVendida - cancelados.quantidadeCancelada);
        const valorDevolvido = positivoOuZero(saida.valorConsignado - vendidos.valorVendido - cancelados.valorCancelado);
        const saldo = positivoOuZero(saida.quantidadeConsignada - entrada.quantidadeDevolvida - vendidos.quantidadeVendida - cancelados.quantidadeCancelada);
        const valorConsignado = positivoOuZero(saida.valorConsignado - entrada.valorDevolvido - vendidos.valorVendido - cancelados.valorCancelado);

        return <>
            <View style={{ borderTop: "1px solid black", marginBottom: '5px' }} />
            <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }} >
                <View style={{ width: '26%', gap: '2px' }}>
                    {dados.relatorioGeral && <>
                        <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                            <Text style={[styles.footerTitle]}>Quantidade aberto:</Text>
                            <Text>{abertos.quantidadeAberta}</Text>
                        </View>
                        <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                            <Text style={[styles.footerTitle]}>Valor Aberto:</Text>
                            <Text>{currencyOf(abertos.valorAberto)}</Text>
                        </View>
                    </>}

                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Quantidade total consignado:</Text>
                        <Text>{saida.quantidadeConsignada}</Text>
                    </View>
                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Valor total consignado:</Text>
                        <Text>{currencyOf(saida.valorConsignado)}</Text>
                    </View>

                    {dados.relatorioGeral && <>
                        <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                            <Text style={[styles.footerTitle]}>Quantidade devolvida:</Text>
                            <Text>{quantidadeDevolvida}</Text>
                        </View>
                        <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                            <Text style={[styles.footerTitle]}>Valor devolvido:</Text>
                            <Text>{currencyOf(valorDevolvido)}</Text>
                        </View>
                    </>
                    }

                </View>

                {dados.relatorioGeral && <View style={{ width: '26%', gap: '2px' }}>
                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Saldo consignado:</Text>
                        <Text>{saldo}</Text>
                    </View>
                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Valor consignado:</Text>
                        <Text>{currencyOf(valorConsignado)}</Text>
                    </View>

                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Quantidade vendida:</Text>
                        <Text>{vendidos.quantidadeVendida}</Text>
                    </View>
                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Valor vendido:</Text>
                        <Text>{currencyOf(vendidos.valorVendido)}</Text>
                    </View>

                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Quantidade cancelada:</Text>
                        <Text>{cancelados.quantidadeCancelada}</Text>
                    </View>
                    <View style={[styles.minText, styles.row, { gap: '5px' }]}>
                        <Text style={[styles.footerTitle]}>Valor cancelado:</Text>
                        <Text>{currencyOf(cancelados.valorCancelado)}</Text>
                    </View>
                </View>
                }
            </View>
        </>;
    };

    const renderObservacoes = () => {
        if (!dados.observacao) return;
        return (
            <View style={{ flexDirection: 'row', border: "1px solid black", borderRadius: 2, padding: 2, marginTop: '7px' }}>
                <View style={{
                    width: "100%",
                    height: "100%",
                    marginBottom: 2
                }}>
                    <Text style={[styles.minText]}>Observação:</Text>
                    <Text style={[styles.minText]}>{dados.observacao}</Text>
                </View>
            </View>
        );
    };

    const renderProdutosAbertos = () => {
        return renderCustomTables({
            itens: dados.abertos ?? [],
            itensTableConfig: [
                {
                    key: 'skuDescritivo',
                    label: 'SKU',
                    sizePercent: '65%',
                },
                {
                    key: 'quantidade',
                    label: 'Quantidade',
                    sizePercent: '7%',
                    textAlign: 'right',
                },
                {
                    key: 'valorUnitario',
                    label: 'Valor unitário',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorUnitario !== undefined ? currencyOf(item.valorUnitario) : undefined
                },
                {
                    key: 'percentualDesconto',
                    label: '% desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.percentualDesconto !== undefined ? `${item.percentualDesconto}%` : undefined
                },
                {
                    key: 'valorDesconto',
                    label: 'Desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorDesconto !== undefined ? currencyOf(item.valorDesconto) : undefined
                },
                {
                    key: 'valorTotalLiquido',
                    label: 'Total',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorTotalLiquido !== undefined ? currencyOf(item.valorTotalLiquido) : undefined
                },
            ],
            totalizadores: ['quantidade', 'valorTotalLiquido',],
        });
    };

    const renderNotasSaida = () => {
        return renderCustomTables({
            itens: dados.notas?.notasSaida ?? [],
            mainRowConfig: mainRowConfigDefault,
            itensTableConfig: [
                {
                    key: 'skuDescritivo',
                    label: 'SKU',
                    sizePercent: '51%',
                },
                {
                    key: 'quantidade',
                    label: 'Quantidade',
                    sizePercent: '7%',
                    textAlign: 'right',
                },
                {
                    key: 'quantidadeDevolvida',
                    label: 'Quantidade devolvida',
                    sizePercent: '7%',
                    textAlign: 'right',
                },
                {
                    key: 'saldo',
                    label: 'Saldo',
                    sizePercent: '7%',
                    textAlign: 'right',
                },
                {
                    key: 'valorUnitario',
                    label: 'Valor unitário',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorUnitario !== undefined ? currencyOf(item.valorUnitario) : undefined
                },
                {
                    key: 'percentualDesconto',
                    label: '% desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.percentualDesconto !== undefined ? `${item.percentualDesconto}%` : undefined
                },
                {
                    key: 'valorDesconto',
                    label: 'Desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorDesconto !== undefined ? currencyOf(item.valorDesconto) : undefined
                },
                {
                    key: 'valorTotalLiquido',
                    label: 'Total',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorTotalLiquido !== undefined ? currencyOf(item.valorTotalLiquido) : undefined
                },
            ],
            totalizadores: ['quantidade', 'quantidadeDevolvida', 'saldo', 'valorTotalLiquido',],
        });
    };

    const renderNotasEntrada = () => {
        return renderCustomTables({
            itens: dados.notas?.notasDevolucao ?? [],
            mainRowConfig: mainRowConfigDefault,
            itensTableConfig: [
                {
                    key: 'skuDescritivo',
                    label: 'SKU',
                    sizePercent: '65%',
                },
                {
                    key: 'quantidade',
                    label: 'Quantidade',
                    sizePercent: '7%',
                    textAlign: 'right',
                },
                {
                    key: 'valorUnitario',
                    label: 'Valor unitário',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorUnitario !== undefined ? currencyOf(item.valorUnitario) : undefined
                },
                {
                    key: 'percentualDesconto',
                    label: '% desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.percentualDesconto !== undefined ? `${item.percentualDesconto}%` : undefined
                },
                {
                    key: 'valorDesconto',
                    label: 'Desconto',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorDesconto !== undefined ? currencyOf(item.valorDesconto) : undefined
                },
                {
                    key: 'valorTotalLiquido',
                    label: 'Total',
                    sizePercent: '7%',
                    textAlign: 'right',
                    convert: item => item.valorTotalLiquido !== undefined ? currencyOf(item.valorTotalLiquido) : undefined
                },
            ],
            totalizadores: ['quantidade', 'valorTotalLiquido'],
        });

    };

    const renderPedidos = (tipoStatusPedido: 'vendidos' | 'cancelados') => {
        const isVendidos = tipoStatusPedido === 'vendidos';
        return renderCustomTables({
            itens: isVendidos ? dados.vendidos : dados.cancelados,
            mainRowConfig: [
                {
                    key: 'numeroPedido',
                    label: 'Pedido',
                    sizePercent: '20%',
                },
                {
                    key: 'numeroEntrega',
                    label: 'Entrega',
                    sizePercent: '20%',
                    textAlign: 'center',
                },
                {
                    key: 'emissao',
                    label: 'Emissão',
                    sizePercent: '20%',
                    textAlign: 'center',
                },
                {
                    key: 'status',
                    label: 'Status',
                    sizePercent: '20%',
                    textAlign: 'center',
                },
                {
                    key: 'valorLiquido',
                    label: 'Total',
                    sizePercent: '20%',
                    textAlign: 'right',
                    convert: item => currencyOf(item.valorLiquido),
                },
            ],
            itensTableConfig: [
                {
                    key: 'sku.descritivo',
                    label: 'SKU',
                    sizePercent: '70%',
                },
                {
                    key: isVendidos ? 'quantidadeVendida' : 'quantidadeCancelada',
                    label: 'Quantidade',
                    sizePercent: '10%',
                    textAlign: 'right',
                },
                {
                    key: 'valorUnitario',
                    label: 'Valor unitário',
                    sizePercent: '10%',
                    textAlign: 'right',
                    convert: item => item.valorUnitario !== undefined ? currencyOf(item.valorUnitario) : undefined,
                },
                {
                    key: 'valorTotalLiquido',
                    label: 'Valor total',
                    sizePercent: '10%',
                    textAlign: 'right',
                    convert: item => item.valorTotalLiquido !== undefined ? currencyOf(item.valorTotalLiquido) : undefined,
                },

            ],
            totalizadores: [isVendidos ? 'quantidadeVendida' : 'quantidadeCancelada', 'valorTotalLiquido'],
        });
    };

    return (
        <>
            <IconButton
                sx={{
                    zIndex: 100000,
                    position: 'absolute',
                    bottom: 20,
                    right: 30,
                    backgroundColor: theme.palette.primary.main,
                    ':hover': {
                        backgroundColor: theme.palette.primary.main,
                        filter: 'grayscale(.2) brightness(.8) contrast(1.5)'
                    }
                }}
                onClick={onClose}
            >
                <CloseIcon sx={{ color: theme.palette.background.default, fontSize: 45 }} />
            </IconButton>

            <PDFViewer
                style={{
                    zIndex: 10000,
                    width: '100%',
                    height: '100vh',
                    border: 0,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                }}
                showToolbar={true}
            >
                <Document
                    title='Romaneio de venda'
                    author='André Franco'
                    language='pt-BR'
                >
                    <Page
                        size={'A4'}
                        orientation='landscape'
                        wrap={true}
                        style={{
                            backgroundColor: theme.palette.common.white,
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: "100vh",
                            padding: 20,
                            paddingBottom: "40px",
                        }}
                        break
                    >
                        <View style={{ width: '100%' }}>
                            {header()}
                            {getTitulo({ title: "Consignado", bigFontSize: true, centralizado: true })}
                            <View style={{ flexDirection: 'row', paddingVertical: '3px', borderBottom: !dados.relatorioGeral ? '1px solid black' : undefined }}>
                                {cabecalhoLeftSide()}
                                {cabecalhoRightSide()}
                            </View>

                            {getTitulo({ title: "Produtos abertos", visible: !!dados.abertos.length })}
                            {renderProdutosAbertos()}

                            {getTitulo({ title: "Saídas", visible: !!dados.notas?.notasSaida.length && dados.relatorioGeral })}
                            {renderNotasSaida()}

                            {getTitulo({ title: "Devolução", visible: !!dados.notas?.notasDevolucao.length })}
                            {renderNotasEntrada()}

                            {getTitulo({ title: "Vendidas", visible: !!dados.vendidos.length })}
                            {renderPedidos('vendidos')}

                            {getTitulo({ title: "Canceladas", visible: !!dados.cancelados.length })}
                            {renderPedidos('cancelados')}

                            {renderTotalizador()}
                            {renderObservacoes()}
                        </View>
                        <View
                            fixed
                            style={{
                                width: '95%',
                                position: 'absolute',
                                bottom: 10,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '10'
                            }}>
                            <View style={{
                                width: '100px',
                                height: '25px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: theme.palette.primary.main,
                                paddingHorizontal: 5,
                                borderRadius: '5px'
                            }}>
                                <Image
                                    src={'assets/logoFlow.png'}
                                />
                            </View>
                            <Text
                                style={{
                                    fontSize: 10,
                                    color: 'grey',
                                }}
                                render={({ pageNumber, totalPages }) => (
                                    `${pageNumber} / ${totalPages}`
                                )}
                            />
                        </View>
                    </Page>
                </Document>
            </PDFViewer >
        </>
    );
};

export default ConsignadoPDF;

Font.register({
    family: 'Work sans',
    src: workSansBold,
    fontWeight: 'bold'
});


const styles = StyleSheet.create({
    minText: {
        fontSize: '8px',
    },
    bold: {
        fontWeight: "bold",
        fontFamily: 'Work sans',
    },
    oneOutOfEight: {
        flex: 1 / 8,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    footerTitle: {
        width: "50%",
        textAlign: 'left'
    }
});

const mainRowConfigDefault: ColumnConfigLocal[] = [
    {
        key: 'serieNumero',
        label: 'Nota',
        sizePercent: '15%',
    },
    {
        key: 'tipo',
        label: 'Tipo',
        sizePercent: '10%',
    },
    {
        key: 'naturezaOperacao',
        label: 'Natureza da operação',
        sizePercent: '45%',
    },
    {
        key: 'emissao',
        label: 'Emissão',
        sizePercent: '15%',
    },

    {
        key: 'situacao',
        label: 'Situação',
        sizePercent: '15%',
    },
];

type ColumnConfigLocal = {
    label: string;
    key: string,
    sizePercent: string;
    textAlign?: 'center' | 'right' | 'left';
    convert?: (item: any) => string | number | undefined;
};

const renderCustomTables = (props: {
    itens: any[];
    totalizadores: string[];
    mainRowConfig?: ColumnConfigLocal[];
    itensTableConfig: ColumnConfigLocal[];
}) => {

    if (!props.itens.length) {
        return <></>;
    }

    const toMap = !!props.mainRowConfig ? props.itens : [{ itens: props.itens }];
    return <View style={{ gap: '5px', marginBottom: '15px' }}>
        {toMap.map((item, index) => {

            const itens = item.itens;

            const totalizadoresInicial = props.totalizadores.reduce((acc, curr) => {
                acc[curr] = 0;
                return acc;
            }, {} as Record<string, number>);

            const totalizadores = itens
                .reduce((acc: any, curr: any) => {
                    for (let prop in acc) {
                        acc[prop] = acc[prop] + curr[prop];
                    }
                    return acc;
                }, totalizadoresInicial);

            return <View key={index}>

                {!!props.mainRowConfig && <>
                    {/* main row */}
                    <View style={{ flexDirection: 'row', paddingVertical: '3px', borderBottom: `1px solid ${theme.palette.text.primary}` }}>
                        {props.mainRowConfig.map(row => {
                            return <Text key={row.key} style={[styles.minText, styles.bold, { width: row.sizePercent, textAlign: row.textAlign }]}>
                                {row.label}
                            </Text>;
                        })}
                    </View>

                    <View style={{
                        flexDirection: 'row',
                        backgroundColor: customColors.cinzaClaro,
                        paddingVertical: '2px'
                    }}>
                        {props.mainRowConfig.map(row => {
                            return <Text key={row.key} style={[styles.minText, { width: row.sizePercent, textAlign: row.textAlign }]}>
                                {row.convert?.(item) ?? resolve(item, row.key)}
                            </Text>;
                        })}
                    </View>
                </>}

                {/* itens table */}
                <View style={{ flexDirection: 'row', gap: '3px', paddingVertical: '3px', alignItems: 'center', borderBottom: `1px solid ${theme.palette.text.primary}` }}>
                    {props.itensTableConfig.map(row => {
                        return <Text key={row.key} style={[styles.minText, styles.bold, { width: row.sizePercent, textAlign: row.textAlign }]}>
                            {row.label}
                        </Text>;
                    })}
                </View>

                {itens.map((item: any, indexItem: number) => {
                    return <View key={indexItem} style={{ flexDirection: 'row', gap: '3px', paddingVertical: '2px' }}>
                        {props.itensTableConfig.map(row => {
                            return <Text key={row.key} style={[styles.minText, { width: row.sizePercent, textAlign: row.textAlign }]}>
                                {row.convert?.(item) ?? resolve(item, row.key)}
                            </Text>;
                        })}
                    </View>;
                })}

                {/* totalizadores */}
                <View style={{ flexDirection: 'row', gap: '3px', paddingVertical: '2px', borderTop: `1px solid ${customColors.cinzaClaro}` }}>
                    {props.itensTableConfig.map((row, indexItem) => {
                        return <Text key={row.key} style={[styles.minText, styles.bold, { width: row.sizePercent, textAlign: row.textAlign }]}>
                            {indexItem === 0 ? 'Total' : row.convert?.(totalizadores) ?? totalizadores[row.key]}
                        </Text>;
                    })}
                </View>;
            </View>;
        })}
    </View>;
};
